import { Link } from 'react-router';
import {
    useStyles,
    Caption,
    Avatar,
    Flex,
    Grid,
    pxToRem,
    useTheme,
    Button,
    useAppDisplayModeContext,
} from '@archipro-design/aria';
import { cn } from '@rocco/utils/cn';
import { ExternalLinkLine, MapPinFill } from '@archipro-design/icons';
import { Check } from 'lucide-react';
import { useTracker } from '@archipro-website/tracker';
import { useWebsiteReferralTracking } from '~/modules/tracking/util/websiteTracking';
import { useWebsiteVisited } from '~/modules/tracking/hook/useWebsiteVisited';
import LocationDisplay from '~/modules/company-details/LocationDisplay';
import type { EnquiryItemType } from '../tracking/util/trackEnquireReveal';
import type { PrimaryCTAResult } from '~/modules/professional/util/professional-cta-helper';
import type { ProfessionalInitialResult } from '~/modules/professional/hook/use-professional-initial-route-data';
import { EnquiryInline } from '~/modules/enquiry/component/enquiry-inline/EnquiryInline';
import * as S from './CompanyProfile.style';

export const COMPANY_ENQUIRY_FORM_ANCHOR = 'request-price';

interface CompanyProfileData {
    Professional: Pick<
        ProfessionalInitialResult,
        | 'ID'
        | 'Title'
        | 'branchOptions'
        | 'CustomEnquiryMessage'
        | 'CompanyLogo'
        | 'LogoBackgroundColor'
        | 'Link'
        | 'LibraryLocations'
    > &
        Partial<
            Pick<
                ProfessionalInitialResult,
                | 'IsManufacturer'
                | 'HasProducts'
                | 'ProductsAllowed'
                | 'EnquiryMessageOverrideEnabled'
                | 'EnquiryMessagePlaceholder'
            >
        >;
    siteTreeID: number;
    primaryCTA?: PrimaryCTAResult;
    website?: string;
    itemType: EnquiryItemType;
}

interface CompanyProfileProps {
    data: CompanyProfileData;
    /** Show a view profile link. Defaults to `true`. */
    showViewProfile?: boolean;
    /** Show an enquiry form. Defaults to `true`. */
    showEnquiryForm?: boolean;
    /** Show a CTA button at the bottom of the page. Defaults to `false`. */
    showCTABottom?: boolean;
    /** Show custom enquiry text from professional settings. Defaults to `true`. */
    showCustomEnquiryText?: boolean;
    /** Show a where to buy link. Defaults to `false`. */
    showWhereToBy?: boolean;
    /**
     * Include the branch dropdown in the enquiry form if the professional has
     * branches. Defaults to `false`.
     */
    includeBranch?: boolean;
    /**
     * Show the company info section (name, locations, logo). Defaults to `true`.
     * When false, only the Visit Website link is shown.
     */
    showCompanyInfo?: boolean;
    /** Remove top margin/padding from the wrapper. Defaults to `false`. */
    removeTopSpacing?: boolean;
    onClickWhereToBuy?: () => void;
}

/**
 * Basic information about a professional. Includes the professional's name,
 * location, logo, an enquiry form, and a link to the professional's website.
 */
const CompanyProfile = ({
    data,
    showViewProfile = true,
    showEnquiryForm = true,
    showCTABottom = false,
    showCustomEnquiryText = true,
    showWhereToBy = false,
    includeBranch = false,
    showCompanyInfo = true,
    removeTopSpacing = false,
    onClickWhereToBuy,
}: CompanyProfileProps) => {
    const { Professional, siteTreeID, primaryCTA, website, itemType } = data;

    const { css } = useStyles();
    const tracker = useTracker();
    const theme = useTheme();
    const { mobile: isMobile } = useAppDisplayModeContext();

    const logoBackgroundColor = Professional?.LogoBackgroundColor || 'white';
    const captionVariables = {
        lineHeight: theme.siteVariables.lineHeightSmall,
    };
    const { referralTrackingURL, referralTrackingParams } =
        useWebsiteReferralTracking(website, {
            libraryItemID: siteTreeID,
            professionalID: Professional.ID,
            professionalTitle: Professional.Title,
        });

    const { hasVisited, markVisited } = useWebsiteVisited(siteTreeID);

    const url = new URL(website || 'https://archipro.co.nz');
    const domain = url.hostname.replace(/^www\./, '');

    const visitWebsiteLabel =
        itemType === 'article' ? 'Visit ' + domain : 'Visit Website';

    return (
        <>
            {isMobile && showCTABottom && (
                <div className={css(S.CTABottomContainer)}>
                    {referralTrackingURL && (
                        <Button
                            as={'a'}
                            href={referralTrackingURL}
                            target={'_blank'}
                            {...referralTrackingParams}
                            onClick={markVisited}
                        >
                            {visitWebsiteLabel}
                            {hasVisited ? (
                                <Check
                                    style={{
                                        width: pxToRem(16),
                                        height: pxToRem(16),
                                        marginLeft: pxToRem(4),
                                    }}
                                    className="text-green-600 inline-block animate-tick-pop-in"
                                />
                            ) : (
                                <ExternalLinkLine
                                    variables={{
                                        '1xSize': pxToRem(16),
                                        color: '#222222',
                                    }}
                                />
                            )}
                        </Button>
                    )}
                </div>
            )}
            <div
                className={cn(
                    css(S.CompanyProfileWrapper),
                    removeTopSpacing && 'mt-0! pt-0!',
                )}
            >
                {showCompanyInfo && (
                    <Grid
                        className={css(S.CompanyProfileContainer)}
                        columns={`1fr ${pxToRem(133)}`}
                        id={COMPANY_ENQUIRY_FORM_ANCHOR}
                    >
                        <div className={css(S.CompanyProfileInfo)}>
                            <Caption variant="03" variables={captionVariables}>
                                {Professional.Title}
                            </Caption>
                            <LocationDisplay
                                Professional={Professional}
                                variables={captionVariables}
                            />
                            {showViewProfile && (
                                <div>
                                    <Caption
                                        as={Link}
                                        to={Professional.Link}
                                        className={css(S.CompanyProfileLink)}
                                        onClick={() => {
                                            tracker.log(
                                                'ProductProfileButtonClick',
                                                {
                                                    url: new URL(
                                                        window.location.href,
                                                    ),
                                                    targetTracker: [
                                                        'archiproTracker',
                                                        'ga4Tracker',
                                                    ],
                                                },
                                            );
                                        }}
                                        link={true}
                                        variant="03"
                                        variables={captionVariables}
                                    >
                                        View Profile
                                    </Caption>
                                </div>
                            )}
                        </div>
                        <Flex hAlign="end">
                            <Avatar
                                as={Link}
                                to={Professional.Link}
                                image={{
                                    src: Professional.CompanyLogo ?? '',
                                    alt: `${Professional.Title} company logo`,
                                }}
                                initials={'Logo'}
                                size={106}
                                imageFit={'contain'}
                                styles={{
                                    borderWidth: 0,
                                }}
                                variables={{
                                    avatarImageBackgroundColor:
                                        logoBackgroundColor,
                                    avatarBackgroundColor: logoBackgroundColor,
                                    borderRadius: pxToRem(4),
                                }}
                            />
                        </Flex>
                    </Grid>
                )}
                {showEnquiryForm && (
                    <EnquiryInline
                        className="mb-8"
                        professional={Professional}
                        submitButtonText={
                            !isMobile ? primaryCTA?.primaryCTALabel : undefined
                        }
                        siteTreeID={siteTreeID}
                        showCustomEnquiryText={showCustomEnquiryText}
                        itemType={itemType}
                        includeBranch={includeBranch}
                        itemTitle={
                            itemType === 'professional'
                                ? Professional.Title
                                : undefined
                        }
                        ctaLabel={
                            itemType === 'professional'
                                ? primaryCTA?.primaryCTALabelTitleCase
                                : undefined
                        }
                    />
                )}
                {showWhereToBy && !isMobile && (
                    <Flex
                        className={css(S.BottomActionContainer)}
                        onClick={onClickWhereToBuy}
                        styles={{
                            marginTop: pxToRem(70),
                            borderBottom: 'unset',
                        }}
                    >
                        <Caption className={css(S.BottomActionLabel)}>
                            Where to buy
                        </Caption>
                        <Flex hAlign="end">
                            <MapPinFill
                                variables={{
                                    '1xSize': pxToRem(24),
                                    color: '#000',
                                }}
                            />
                        </Flex>
                    </Flex>
                )}
                {!isMobile && referralTrackingURL && (
                    <Flex
                        className={css(S.BottomActionContainer)}
                        as={'a'}
                        href={referralTrackingURL}
                        target={'_blank'}
                        styles={{
                            marginTop:
                                showWhereToBy || !showEnquiryForm
                                    ? 0
                                    : pxToRem(70),
                        }}
                        {...referralTrackingParams}
                        onClick={markVisited}
                    >
                        <Caption className={css(S.BottomActionLabel)}>
                            {visitWebsiteLabel}
                        </Caption>
                        <Flex hAlign="end">
                            {hasVisited ? (
                                <Check
                                    style={{
                                        width: pxToRem(24),
                                        height: pxToRem(24),
                                    }}
                                    className="text-green-600 inline-block animate-tick-pop-in"
                                />
                            ) : (
                                <ExternalLinkLine
                                    variables={{
                                        '1xSize': pxToRem(24),
                                        color: '#000',
                                    }}
                                />
                            )}
                        </Flex>
                    </Flex>
                )}
            </div>
        </>
    );
};

export default CompanyProfile;
